import React, { useMemo, useState, useEffect } from "react"
import objectPath from "object-path"
import { SvgIcon } from "@material-ui/core"
import useMediaQuery from "@mui/material/useMediaQuery"
import { useHtmlClassService } from "../_core/MetronicLayout"
// Import Layout components
import { Header } from "./header/Header"
import { HeaderMobile } from "./header-mobile/HeaderMobile"
import { Footer } from "./footer/Footer"
import { LayoutInit } from "./LayoutInit"
import { QuickPanel } from "./extras/offcanvas/QuickPanel"
import { QuickUser } from "./extras/offcanvas/QuickUser"
import { ScrollTop } from "./extras/ScrollTop"
import Sidebar from "./Sidebar"
import { shallowEqual, useSelector } from "react-redux"

export function Layout({ children }) {
	const uiService = useHtmlClassService()
	const matches = useMediaQuery("(min-width:992px)")

	const { selectedSite, userClaims, permissions } = useSelector(
		state => ({
			selectedSite: state.profile?.currentSite,
			userClaims: state.auth?.claims,
			permissions: state.auth?.permissions?.permissions
		}),
		shallowEqual
	)

	const layoutProps = useMemo(() => {
		return {
			layoutConfig: uiService.config,
			selfLayout: objectPath.get(uiService.config, "self.layout"),
			asideDisplay: objectPath.get(uiService.config, "aside.self.display"),
			subheaderDisplay: objectPath.get(uiService.config, "subheader.display"),
			desktopHeaderDisplay: objectPath.get(uiService.config, "header.self.fixed.desktop"),
			contentCssClasses: uiService.getClasses("content", true),
			contentContainerClasses: uiService.getClasses("content_container", true),
			contentExtended: objectPath.get(uiService.config, "content.extended")
		}
	}, [uiService])

	const [hideDrawer, setHideDrawer] = useState(true)

	const handleHideDrawer = () => {
		setHideDrawer(prev => !prev)
	}

	useEffect(() => {
		if (matches) {
			setHideDrawer(true)
			return
		}

		setHideDrawer(false)
	}, [matches])

	const options = [
		permissions &&
			permissions.Dashboard && {
				label: { title: "Dashboard", icon: <HomeIcon />, redirectTo: "/dashboard" },
				nestedOptions: [
					permissions.Dashboard.PeopleAndAssets && {
						title: "People & Assets",
						redirectTo: "/dashboard"
					},
					selectedSite &&
						permissions.Dashboard.Sensors && {
							title: "Sensors",
							redirectTo: "/sensors"
						}
				].filter(Boolean)
			},

		selectedSite &&
			permissions &&
			permissions.Reporting && {
				label: { title: "Reporting", icon: <ReportingIcon />, redirectTo: "/reporting" },
				nestedOptions: [
					permissions &&
						permissions.Reporting.People && {
							title: "People",
							nestedOptions: [
								permissions &&
									permissions.Reporting.People.Summary && {
										title: "Summary",
										redirectTo: "/reporting?peopleSummary"
									},
								permissions &&
									permissions.Reporting.People.Timesheets && {
										title: "Timesheets",
										redirectTo: "/reporting?peopleTimesheet"
									}
							].filter(Boolean)
						},
					permissions &&
						permissions.Reporting.Assets && {
							title: "Assets",
							redirectTo: "/reporting?assetsSummary"
						}
				].filter(Boolean)
			},

		selectedSite &&
			userClaims?.profileLvl >= 3 &&
			permissions &&
			permissions.AuditTool && {
				label: { title: "Audit Tool", icon: <AuditIcon />, redirectTo: "/audit-tool" }
			},

		selectedSite &&
			!selectedSite?.siteInfo?.siteFinished &&
			permissions &&
			permissions.Communication && {
				label: {
					title: "Communication",
					icon: <CommunicationIcon />,
					redirectTo: "/communication"
				}
			},

		selectedSite &&
			permissions &&
			permissions.Admin && {
				label: { title: "Admin", icon: <AdminIcon />, redirectTo: "/admin" },
				nestedOptions: [
					permissions &&
						userClaims?.profileLvl >= 3 &&
						permissions.Admin.CustomerOnboarding && {
							title: "Customer Onboarding",
							redirectTo: "/admin/customer-on-boarding"
						},
					permissions &&
						permissions.Admin.ManageCustomer && {
							title: "Manage Customer",
							nestedOptions: [
								permissions &&
									permissions.Admin.ManageCustomer.Configuration &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "Configuration",
										redirectTo: "/admin/manage-customer/configuration"
									},
								permissions &&
									permissions.Admin.ManageCustomer.Sites && {
										title: "Sites",
										redirectTo: "/admin/manage-customer/sites"
									},
								permissions &&
									permissions.Admin.ManageCustomer.PlatformUsers &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "Users",
										redirectTo: "/admin/manage-customer/users"
									},
								permissions &&
									permissions.Admin.ManageCustomer.People &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "People",
										redirectTo: "/admin/manage-customer/people"
									},
								permissions &&
									permissions.Admin.ManageCustomer.Assets &&
									!selectedSite?.siteInfo?.siteFinished && {
										title: "Assets",
										redirectTo: "/admin/manage-customer/assets"
									}
							].filter(Boolean)
						},
					permissions &&
						permissions.Admin.ManageSite &&
						!selectedSite?.siteInfo?.siteFinished && {
							title: "Manage Site",
							redirectTo: "/admin/manage-site"
						},
					permissions &&
						permissions.Admin.DesignStudio &&
						!selectedSite?.siteInfo?.siteFinished && {
							title: "Design Studio",
							redirectTo: "/admin/design-studio"
						}
				].filter(Boolean)
			},

		//for making sure customer onboarding is avalilable when there are no customers
		!selectedSite &&
			permissions &&
			permissions.Admin && {
				label: { title: "Admin", icon: <AdminIcon />, redirectTo: "/admin" },
				nestedOptions: [
					permissions &&
						userClaims?.profileLvl >= 3 &&
						permissions.Admin.CustomerOnboarding && {
							title: "Customer Onboarding",
							redirectTo: "/admin/customer-on-boarding"
						}
				].filter(Boolean)
			},
		//will be used in the feature
		//selectedSite && {
		// 	label: {
		// 		title: "Rules Engine",
		// 		icon: <RulesIcon />,
		// 		redirectTo: "/rules-engine"
		// 	}
		// },
		selectedSite &&
			userClaims?.profileLvl >= 4 &&
			permissions &&
			permissions.Operations && {
				label: { title: "Operations", icon: <OperationIcon />, redirectTo: "/operations" },
				nestedOptions: [
					permissions.Operations.Inventory && {
						title: "Inventory",
						redirectTo: "/operations/inventory"
					},
					permissions.Operations.MapInfrastructure && {
						title: "Manage Infrasctructure",
						redirectTo: "/operations/manage-infrastructure"
					}
				].filter(Boolean)
			}
	].filter(Boolean)

	return layoutProps.selfLayout !== "blank" ? (
		<>
			{/*begin::Main*/}
			<HeaderMobile handleHideDrawer={handleHideDrawer} />
			<div className="d-flex flex-column flex-root">
				{/*begin::Page*/}
				<div className="d-flex flex-row flex-column-fluid page">
					{/* {layoutProps.asideDisplay && <Aside />} */}
					<Sidebar
						options={options}
						hideDrawer={hideDrawer}
						handleHideDrawer={handleHideDrawer}
						matches={matches}
					/>
					{/*begin::Wrapper*/}
					<div className="d-flex flex-column flex-row-fluid wrapper" id="kt_wrapper">
						<Header />
						{/*begin::Content*/}
						<div
							id="kt_content"
							className={`content ${layoutProps.contentCssClasses} d-flex flex-column flex-column-fluid`}
						>
							{/* {layoutProps.subheaderDisplay && <SubHeader/>} */}
							{/*begin::Entry*/}
							{!layoutProps.contentExtended && (
								<div className="d-flex flex-column-fluid">
									{/*begin::Container*/}
									<div className={layoutProps.contentContainerClasses}>{children}</div>
									{/*end::Container*/}
								</div>
							)}

							{layoutProps.contentExtended && { children }}
							{/*end::Entry*/}
						</div>
						{/*end::Content*/}
						<Footer />
					</div>
					{/*end::Wrapper*/}
				</div>
				{/*end::Page*/}
			</div>
			<QuickUser />
			<QuickPanel />
			<ScrollTop />
			{/*end::Main*/}
			<LayoutInit />
		</>
	) : (
		// BLANK LAYOUT
		<div className="d-flex flex-column flex-root">{children}</div>
	)
}

function HomeIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				width="19"
				height="20"
				viewBox="0 0 19 20"
				fill="#8C8CA1"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g id="home">
					<path
						id="Vector"
						d="M9.86355 2.30159C9.75816 2.21935 9.62831 2.17468 9.49463 2.17468C9.36094 2.17468 9.23109 2.21935 9.1257 2.30159L0.59375 8.95492L1.3316 9.88811L2.375 9.07456V16.4246C2.37564 16.7394 2.50096 17.0411 2.72352 17.2636C2.94608 17.4862 3.24775 17.6115 3.5625 17.6121H15.4375C15.7523 17.6116 16.054 17.4863 16.2765 17.2637C16.4991 17.0411 16.6244 16.7394 16.625 16.4246V9.07996L17.6684 9.8934L18.4062 8.96014L9.86355 2.30159ZM10.6875 16.4246H8.3125V11.6746H10.6875V16.4246ZM11.875 16.4246V11.6746C11.8746 11.3598 11.7494 11.058 11.5268 10.8354C11.3042 10.6127 11.0023 10.4875 10.6875 10.4871H8.3125C7.99765 10.4875 7.69579 10.6127 7.47316 10.8353C7.25053 11.0579 7.12531 11.3598 7.125 11.6746V16.4246H3.5625V8.14866L9.5 3.52335L15.4375 8.1549V16.4246H11.875Z"
					/>
				</g>
			</svg>
		</SvgIcon>
	)
}

function ReportingIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				width="18"
				height="18"
				viewBox="0 0 18 18"
				fill="#8C8CA1"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g id="chart--ring">
					<path
						id="Vector"
						d="M8.70405 0.177673C7.01771 0.177673 5.36923 0.677733 3.96709 1.61462C2.56494 2.5515 1.4721 3.88313 0.826765 5.44111C0.181429 6.99909 0.0125792 8.71345 0.341569 10.3674C0.670559 12.0213 1.48261 13.5406 2.67504 14.733C3.86746 15.9254 5.38671 16.7375 7.04065 17.0665C8.69459 17.3955 10.409 17.2266 11.9669 16.5813C13.5249 15.9359 14.8565 14.8431 15.7934 13.441C16.7303 12.0388 17.2304 10.3903 17.2304 8.70399C17.2278 6.44345 16.3287 4.27623 14.7303 2.67779C13.1318 1.07934 10.9646 0.18022 8.70405 0.177673ZM15.9814 8.09557H12.3034C12.2019 7.48531 11.9467 6.91094 11.5618 6.4266C11.1769 5.94226 10.675 5.56389 10.1035 5.32716C9.53192 5.09043 8.9095 5.00313 8.29487 5.07349C7.68024 5.14386 7.09365 5.36956 6.59038 5.72934L3.98985 3.12881C5.01262 2.26064 6.25498 1.69131 7.58033 1.48342C8.90568 1.27554 10.2627 1.43715 11.5021 1.95048C12.7416 2.46381 13.8155 3.30899 14.6058 4.39308C15.396 5.47717 15.8721 6.75818 15.9815 8.09527L15.9814 8.09557ZM8.70405 11.1401C8.22224 11.1401 7.75125 10.9972 7.35063 10.7295C6.95002 10.4618 6.63778 10.0814 6.4534 9.63624C6.26902 9.1911 6.22077 8.70129 6.31477 8.22873C6.40877 7.75617 6.64078 7.3221 6.98148 6.98141C7.32217 6.64072 7.75624 6.4087 8.22879 6.31471C8.70135 6.22071 9.19117 6.26895 9.6363 6.45333C10.0814 6.63772 10.4619 6.94996 10.7296 7.35057C10.9973 7.75118 11.1401 8.22217 11.1401 8.70399C11.1394 9.34986 10.8825 9.96906 10.4258 10.4258C9.96913 10.8825 9.34992 11.1394 8.70405 11.1401ZM1.39578 8.70399C1.39512 6.97673 2.00946 5.30567 3.12876 3.99015L5.72928 6.59037C5.36963 7.09367 5.14401 7.68023 5.07368 8.29481C5.00335 8.90939 5.09062 9.53176 5.32728 10.1033C5.56394 10.6748 5.94219 11.1767 6.4264 11.5617C6.91062 11.9466 7.48486 12.202 8.09503 12.3037V15.9822C6.26991 15.8281 4.56901 14.9948 3.32857 13.6472C2.08813 12.2996 1.39842 10.5356 1.39578 8.70399ZM9.31307 15.9818V12.3033C10.0598 12.1758 10.7484 11.8196 11.2841 11.284C11.8197 10.7484 12.1759 10.0597 12.3034 9.31301H15.9819C15.836 11.0328 15.0865 12.6455 13.866 13.866C12.6456 15.0864 11.0329 15.8359 9.31307 15.9818Z"
					/>
				</g>
				<defs>
					<clipPath id="clip0_4926_1832">
						<rect width="18" height="18" />
					</clipPath>
				</defs>
			</svg>
		</SvgIcon>
	)
}

function AuditIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M3.75 19.6973H8.75V18.4473H3.75V3.44727H13.75V9.69727H15V3.44727C14.9997 3.11585 14.8678 2.79811 14.6335 2.56377C14.3992 2.32942 14.0814 2.19761 13.75 2.19727H3.75C3.41859 2.19761 3.10085 2.32942 2.8665 2.56377C2.63216 2.79811 2.50035 3.11585 2.5 3.44727V18.4473C2.50035 18.7787 2.63216 19.0964 2.8665 19.3308C3.10085 19.5651 3.41859 19.6969 3.75 19.6973ZM12.5 4.69727H5V5.94727H12.5V4.69727ZM8.75 9.69727H5V10.9473H8.75V9.69727ZM5 7.19727H12.5V8.44727H5V7.19727ZM8.75 15.9473H5V17.1973H8.75V15.9473ZM16.875 16.5723C17.3723 16.5723 17.8492 16.7698 18.2008 17.1214C18.5525 17.4731 18.75 17.95 18.75 18.4473V19.6973H17.5V18.4473C17.5 18.2815 17.4342 18.1225 17.3169 18.0053C17.1997 17.8881 17.0408 17.8223 16.875 17.8223H13.125C12.9592 17.8223 12.8003 17.8881 12.6831 18.0053C12.5658 18.1225 12.5 18.2815 12.5 18.4473V19.6973H11.25V18.4473C11.25 17.95 11.4475 17.4731 11.7992 17.1214C12.1508 16.7698 12.6277 16.5723 13.125 16.5723H16.875ZM15 15.9473C15.4945 15.9473 15.9778 15.8006 16.3889 15.5259C16.8 15.2512 17.1205 14.8608 17.3097 14.404C17.4989 13.9472 17.5484 13.4445 17.452 12.9595C17.3555 12.4746 17.1174 12.0291 16.7678 11.6795C16.4181 11.3299 15.9727 11.0918 15.4877 10.9953C15.0028 10.8988 14.5001 10.9483 14.0433 11.1376C13.5865 11.3268 13.196 11.6472 12.9213 12.0583C12.6466 12.4695 12.5 12.9528 12.5 13.4473C12.5 14.1103 12.7634 14.7462 13.2322 15.215C13.7011 15.6839 14.337 15.9473 15 15.9473ZM15 12.1973C15.2472 12.1973 15.4889 12.2706 15.6945 12.4079C15.9 12.5453 16.0602 12.7405 16.1549 12.9689C16.2495 13.1973 16.2742 13.4487 16.226 13.6911C16.1778 13.9336 16.0587 14.1563 15.8839 14.3312C15.7091 14.506 15.4863 14.625 15.2439 14.6732C15.0014 14.7215 14.7501 14.6967 14.5216 14.6021C14.2932 14.5075 14.098 14.3473 13.9607 14.1417C13.8233 13.9362 13.75 13.6945 13.75 13.4473C13.75 13.1157 13.8817 12.7978 14.1161 12.5634C14.3505 12.329 14.6685 12.1973 15 12.1973Z"
				/>
			</svg>
		</SvgIcon>
	)
}

function CommunicationIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M11.0875 19.2109L10 18.5859L12.5 14.2109H16.25C16.5815 14.2109 16.8995 14.0792 17.1339 13.8448C17.3683 13.6104 17.5 13.2925 17.5 12.9609V5.46094C17.5 5.12942 17.3683 4.81147 17.1339 4.57705C16.8995 4.34263 16.5815 4.21094 16.25 4.21094H3.75C3.41848 4.21094 3.10054 4.34263 2.86612 4.57705C2.6317 4.81147 2.5 5.12942 2.5 5.46094V12.9609C2.5 13.2925 2.6317 13.6104 2.86612 13.8448C3.10054 14.0792 3.41848 14.2109 3.75 14.2109H9.375V15.4609H3.75C3.08696 15.4609 2.45107 15.1975 1.98223 14.7287C1.51339 14.2599 1.25 13.624 1.25 12.9609V5.46094C1.25 4.7979 1.51339 4.16201 1.98223 3.69317C2.45107 3.22433 3.08696 2.96094 3.75 2.96094H16.25C16.913 2.96094 17.5489 3.22433 18.0178 3.69317C18.4866 4.16201 18.75 4.7979 18.75 5.46094V12.9609C18.75 13.624 18.4866 14.2599 18.0178 14.7287C17.5489 15.1975 16.913 15.4609 16.25 15.4609H13.225L11.0875 19.2109ZM15 6.71094H5V7.96094H15V6.71094ZM5 10.4609H11.25V11.7109H5V10.4609Z"
				/>
			</svg>
		</SvgIcon>
	)
}

function AdminIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="21"
				height="20"
				viewBox="0 0 21 20"
				fill="#8C8CA1"
			>
				<g clipPath="url(#clip0_13186_137494)">
					<path d="M15.7501 6.98339H14.8751V5.38546C14.8751 3.18031 12.9151 1.39062 10.5001 1.39062C8.08506 1.39062 6.12506 3.18031 6.12506 5.38546H7.87506C7.87506 4.05917 9.04756 2.98856 10.5001 2.98856C11.9526 2.98856 13.1251 4.05917 13.1251 5.38546V6.98339H5.25006C4.28756 6.98339 3.50006 7.70246 3.50006 8.58132V16.571C3.50006 17.4498 4.28756 18.1689 5.25006 18.1689H15.7501C16.7126 18.1689 17.5001 17.4498 17.5001 16.571V8.58132C17.5001 7.70246 16.7126 6.98339 15.7501 6.98339ZM15.7501 16.571H5.25006V8.58132H15.7501V16.571ZM10.5001 14.1741C11.4626 14.1741 12.2501 13.455 12.2501 12.5762C12.2501 11.6973 11.4626 10.9782 10.5001 10.9782C9.53756 10.9782 8.75006 11.6973 8.75006 12.5762C8.75006 13.455 9.53756 14.1741 10.5001 14.1741Z" />
				</g>
				<defs>
					<clipPath id="clip0_13186_137494">
						<rect width="21" height="19.1752" transform="translate(0 0.591797)" />
					</clipPath>
				</defs>
			</svg>
		</SvgIcon>
	)
}

function OperationIcon(props) {
	return (
		<SvgIcon {...props}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="21"
				viewBox="0 0 20 21"
				fill="#8C8CA1"
			>
				<path d="M18.3217 15.4609V14.2109H17.0085C16.9279 13.8197 16.7726 13.4477 16.551 13.1154L17.4823 12.1841L16.5985 11.3004L15.6673 12.2316C15.3349 12.0101 14.9629 11.8547 14.5717 11.7741V10.4609H13.3217V11.7741C12.9304 11.8547 12.5585 12.01 12.2261 12.2316L11.2948 11.3003L10.4111 12.1841L11.3423 13.1153C11.1208 13.4477 10.9654 13.8197 10.8848 14.2109H9.57166V15.4609H10.8848C10.9654 15.8521 11.1208 16.2241 11.3423 16.5565L10.411 17.4878L11.2948 18.3715L12.226 17.4403C12.5584 17.6618 12.9304 17.8171 13.3217 17.8977V19.2109H14.5717V17.8977C14.9629 17.8172 15.3349 17.6618 15.6672 17.4403L16.5985 18.3716L17.4822 17.4878L16.551 16.5566C16.7725 16.2242 16.9279 15.8522 17.0085 15.4609H18.3217ZM13.9467 16.7109C13.5758 16.7109 13.2133 16.601 12.905 16.3949C12.5966 16.1889 12.3563 15.8961 12.2144 15.5535C12.0725 15.2109 12.0353 14.8339 12.1077 14.4701C12.18 14.1064 12.3586 13.7723 12.6208 13.5101C12.8831 13.2479 13.2171 13.0693 13.5809 12.997C13.9446 12.9246 14.3216 12.9617 14.6642 13.1037C15.0068 13.2456 15.2996 13.4859 15.5057 13.7942C15.7117 14.1026 15.8217 14.4651 15.8217 14.8359C15.8211 15.3331 15.6234 15.8096 15.2719 16.1612C14.9204 16.5127 14.4438 16.7104 13.9467 16.7109Z" />
				<path d="M14.9189 3.16992H13.0439V2.54492C13.043 2.2137 12.911 1.89631 12.6768 1.66209C12.4426 1.42788 12.1252 1.29588 11.7939 1.29492H6.79394C6.46272 1.29588 6.14533 1.42788 5.91112 1.66209C5.6769 1.89631 5.5449 2.2137 5.54395 2.54492V3.16992H3.66895C3.33772 3.17088 3.02033 3.30288 2.78612 3.53709C2.5519 3.77131 2.4199 4.0887 2.41895 4.41992V17.5449C2.4199 17.8761 2.5519 18.1935 2.78612 18.4277C3.02033 18.662 3.33772 18.794 3.66895 18.7949H8.04394V17.5449H3.66895V4.41992H5.54395V6.29492H13.0439V4.41992H14.9189V8.16992H16.1689V4.41992C16.168 4.0887 16.036 3.77131 15.8018 3.53709C15.5676 3.30288 15.2502 3.17088 14.9189 3.16992ZM11.7939 5.04492H6.79394V2.54492H11.7939V5.04492Z" />
			</svg>
		</SvgIcon>
	)
}
